import * as React from "react";
import { Grow } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";

import { useAnimation } from "../core/animation-provider";
import { CustomSkeleton } from "../customSkeleton/custom-skeleton";

export interface StatisticCardProps {
	title: string;
	value?: number | string | React.ReactNode;
	icon: React.ReactNode;
	additonalInfo?: string | React.ReactNode;
	isLoading?: boolean;
	animationDelay: number;
	timeout: number;
	isLast: boolean;
	valueStyle?: any;
}

export function StatisticCard({
	title,
	value,
	icon,
	additonalInfo,
	isLoading,
	animationDelay = 0,
	timeout = 0,
	isLast = false,
	valueStyle,
}: StatisticCardProps): React.JSX.Element {
	const { isAnimated, setIsAnimated } = useAnimation();
	const [visible, setVisible] = React.useState(isAnimated);
	const [displayValue, setDisplayValue] = React.useState(0);

	React.useEffect(() => {
		const timer = setTimeout(() => {
			setVisible(true);

			if (isLast) {
				setTimeout(() => {
					setIsAnimated(true);
				}, timeout);
			}
		}, animationDelay);

		return () => clearTimeout(timer);
	}, [animationDelay, timeout, isLast, setIsAnimated]);

	React.useLayoutEffect(() => {
		if (!visible || isLoading || typeof value !== "number") return;
		if (isAnimated) {
			setDisplayValue(value);
			return;
		}

		let start: number | null = null;
		const duration = timeout;
		const step = (timestamp: number) => {
			if (!start) start = timestamp;
			const progress = Math.min((timestamp - start) / duration, 1);
			if (value.toString().includes(".")) {
				const decimalPlaces = value.toString().split(".")[1]?.length || 0;
				const newValue = Number((progress * value).toFixed(decimalPlaces));
				setDisplayValue(newValue);
			} else {
				setDisplayValue(Math.round(progress * value));
			}
			if (progress < 1) {
				requestAnimationFrame(step);
			} else {
				setDisplayValue(value);
			}
		};
		requestAnimationFrame(step);
	}, [visible, value, isLoading, timeout, isAnimated]);

	return (
		<Grow in={visible} timeout={isAnimated ? 0 : timeout}>
			<Card sx={{ minHeight: "132px" }}>
				<CardContent sx={{ p: 3, pb: "16px !important", pt: 2 }}>
					<Box display="flex" justifyContent="space-between" gap={2} flexDirection="column">
						<Box display="flex" justifyContent="space-between" alignItems="center">
							<Typography color="text.secondary" fontSize={14} fontWeight={500}>
								{title}
							</Typography>
							{icon}
						</Box>
						<Box display="flex" flexDirection="column">
							<Typography fontSize={22} fontWeight={600} minHeight={28}>
								{isLoading ? (
									<CustomSkeleton variant="text" width={28} height={32} sx={{ mt: 0 }} />
								) : typeof value === "number" ? (
									<Box sx={valueStyle}>{displayValue}</Box>
								) : (
									value
								)}
							</Typography>
							<Typography color="text.secondary" fontSize={12}>
								{isLoading && additonalInfo ? (
									<CustomSkeleton variant="rounded" width={100} height={16} sx={{ mt: 0.5, mb: 0.5 }} />
								) : (
									additonalInfo
								)}
							</Typography>
						</Box>
					</Box>
				</CardContent>
			</Card>
		</Grow>
	);
}
